import React, { useState, useEffect } from "react";
import { Modal } from "../../../components/Modal";
import type { SignRequest } from "../../../types/signRequest";
import type { ApprovalRequest } from "../../../types/legalOps";
import { FileUploadKit } from "../../../components/common/FileUploadKit";
import { useAiContext } from "../../../ai/AiContext";
import { useAuth } from "../../../context/AuthContext";
import { ApprovalFlowPanel } from "../../../components/approval/ApprovalFlowPanel";
import { useApprovalFlow } from "../../../hooks/useApprovalFlow";
import { useToast } from "../../../components/Toast";
import { updateSignRequest } from "../services/signRequestService";
import { deleteApprovalRequest, updateApprovalRequest } from "../../../services/approvalRequestsService";
import { updateStageDecision } from "../../../services/approvalFlowService"; 
import { isSuperUser, isLisa, isIrene } from "../../../config/roles";
import { serverTimestamp } from "../../../../firebaseClient";
import { probeFlowForRequest } from "../../../services/contractFlowRescueReader";
import { submitDirectSealGateDecision } from "../../../services/directSealGateService";

// [Synced] Approval Engine v4.6.1

type UnifiedRequest = SignRequest | ApprovalRequest;

interface Props {
  request: UnifiedRequest;
  onClose: () => void;
  onUpdate: () => void;
}

export function SignRequestDetailModal({ request, onClose, onUpdate }: Props) {
  const { user } = useAuth();
  const { setActiveItem, requestDelete } = useAiContext();
  const [currentRequest, setCurrentRequest] = useState<UnifiedRequest>(request);
  const [isProcessing, setIsProcessing] = useState(false);
  const { showToast } = useToast();

  const isSuper = isSuperUser(user?.email);

  // --- Flow Resolution Logic (Rescue) ---
  const [resolvedFlowId, setResolvedFlowId] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    setResolving(true);
    // Determine context module for rescue probe
    const module = (request as any).module || "contractDraft";
    
    probeFlowForRequest(request.id, module)
      .then(flow => {
          if (flow) setResolvedFlowId(flow.id);
          else setResolvedFlowId((request as any).approvalFlowId || null);
      })
      .catch(e => console.error("[DetailModal] Resolve Error", e))
      .finally(() => setResolving(false));
  }, [request]);

  // Determine flow type based on module
  const flowType = (request as any).module === "sealRequest" || (request as any).module === "SEAL_REQUEST" ? "SEAL_REQUEST" : "CONTRACT_REVIEW";

  const { flow, loading: flowLoading } = useApprovalFlow(
      flowType, 
      request.id, 
      resolvedFlowId
  );

  // --- Repair Button Logic ---
  const showRepair = isSuper && resolvedFlowId && (request as any).approvalFlowId !== resolvedFlowId;

  const handleRepairLink = async () => {
      if (!resolvedFlowId) return;
      if (!confirm(`確定強制關聯流程 ID: ${resolvedFlowId} 到此案件？`)) return;
      try {
          if ('module' in currentRequest) {
              await updateApprovalRequest(currentRequest.id, { approvalFlowId: resolvedFlowId } as any);
          } else {
              await updateSignRequest(currentRequest.id, { approvalFlowId: resolvedFlowId } as any);
          }
          showToast("關聯已建立");
          onUpdate(); 
      } catch (e: any) {
          console.error(e);
          showToast("修復失敗", "error");
      }
  };

  // Field Normalization Helpers
  const reqTitle = currentRequest.title;
  const reqId = currentRequest.id;
  const reqStatus = currentRequest.status;
  const requesterName = (currentRequest as any).requesterName || (currentRequest as any).createdByUserName || "User";
  const reqDeptName = (currentRequest as any).department || (currentRequest as any).createdByDeptName || "Dept";
  const reqCategory = (currentRequest as any).docCategory || (currentRequest as any).category || "一般";
  const reqAmount = (currentRequest as any).contractAmount || (currentRequest as any).amount || 0;
  const reqDescription = currentRequest.description;
  const reqConsultedDepts = (currentRequest as any).consultedDepts;
  const reqOtherComments = (currentRequest as any).otherDeptComments;
  const reqAttachments = currentRequest.attachments;
  const reqReviewAttachments = currentRequest.reviewAttachments;

  // Direct Seal Gate Data
  const gate = (currentRequest as any).directSealGate;
  const showGateUI = gate && gate.enabled && (isLisa(user?.email) || isIrene(user?.email)) && (reqStatus === 'COMPLETED' || reqStatus === 'FULLY_APPROVED');
  const [gateNote, setGateNote] = useState("");

  const handleGateDecision = async (decision: "ALLOW" | "DENY") => {
      let actorKey: "lisa" | "irene" | null = null;
      if (isLisa(user?.email)) actorKey = "lisa";
      else if (isIrene(user?.email)) actorKey = "irene";
      
      if (!actorKey) return;
      if (decision === 'DENY' && !confirm("否決將導致此案退回需補件狀態，確定嗎？")) return;

      setIsProcessing(true);
      try {
          await submitDirectSealGateDecision(currentRequest.id, actorKey, decision, gateNote);
          showToast(`已提交決定: ${decision}`);
          onUpdate();
      } catch (e: any) {
          showToast(`Error: ${e.message}`, "error");
      } finally {
          setIsProcessing(false);
      }
  };

  useEffect(() => {
    setCurrentRequest(request);
    setActiveItem({
        type: "signRequest",
        id: request.id,
        title: request.title,
        data: request,
        attachments: request.attachments
    });
    return () => setActiveItem(null);
  }, [request, setActiveItem]);

  const handleDecision = async (
    stageKey: string,
    decision: "APPROVED" | "REJECTED" | "DONE" | "RETURNED",
    comment: string,
    deptCode?: string
  ) => {
      if (!flow) return;
      setIsProcessing(true);
      try {
          const safeDecision = decision as "APPROVED" | "REJECTED" | "RETURNED" | "DONE";
          
          await updateStageDecision({
              flowId: flow.id,
              stageKey: stageKey as any,
              decision: safeDecision,
              comment,
              actorUid: user?.id || 'unknown',
              actorName: user?.displayName || 'User',
              deptCode
          });
          
          let newStatus = currentRequest.status;
          let completedAt = undefined;

          if (decision === 'REJECTED') {
              newStatus = 'REJECTED'; 
              completedAt = serverTimestamp();
          } else if (decision === 'RETURNED') {
              newStatus = (stageKey === 'LEGAL_REVIEW') ? 'LEGAL_RETURNED' : 'RETURNED'; 
              completedAt = serverTimestamp();
          } else if (decision === 'APPROVED') {
              if (stageKey === 'MANAGER_IRENE') {
                  newStatus = 'FULLY_APPROVED'; 
                  completedAt = serverTimestamp();
              } else {
                  if (stageKey === 'LEGAL_REVIEW' || stageKey === 'MANAGER_LISA') {
                      newStatus = 'PENDING_MANAGER'; 
                  }
              }
          }

          if (newStatus !== currentRequest.status || completedAt) {
              const updates: any = { status: newStatus as any };
              if (completedAt) updates.completedAt = completedAt;
              
              if ('module' in currentRequest) {
                  await updateApprovalRequest(currentRequest.id, updates);
              } else {
                  await updateSignRequest(currentRequest.id, updates);
              }
              setCurrentRequest(prev => ({ ...prev, ...updates }));
          }
          
          showToast(`已執行: ${decision === 'APPROVED' ? '核准' : decision === 'REJECTED' ? '駁回' : '退回'}`);
          setTimeout(() => { onUpdate(); }, 500); 

      } catch(e: any) {
          console.error("[SignRequest] Decision Failed", e);
          let msg = e.message || "Unknown Error";
          if (e.details && e.details.originalMessage) {
              msg = e.details.originalMessage;
          }
          showToast(`操作失敗: ${msg}`, "error");
      } finally {
          setIsProcessing(false);
      }
  };

  const handleDelete = () => {
      const s = currentRequest.status as string;
      if (s === 'APPROVED' || s === 'FULLY_APPROVED' || s === 'PUBLISHED') {
          alert("此案件已完成簽核（或已用印），不可刪除，請改用作廢流程。");
          return;
      }
      requestDelete({
          targetType: 'sealRequest',
          targetId: currentRequest.id,
          summary: `用印申請：${currentRequest.title}`,
          onConfirm: async () => {
              try {
                  await deleteApprovalRequest(currentRequest.id, { uid: user?.id||'unknown', name: user?.displayName||'User' });
                  showToast("已刪除申請單");
                  onClose();
                  onUpdate();
              } catch(e: any) {
                  console.error(e);
                  showToast(e.message || "刪除失敗", "error");
              }
          }
      });
  };

  const statusColor = (s: string) => {
      if(s === 'APPROVED' || s === 'FULLY_APPROVED') return 'var(--ok)';
      if(s === 'REJECTED' || s === 'RETURNED' || s === 'LEGAL_RETURNED') return 'var(--danger)';
      if(s === 'PENDING_MANAGER') return '#8b5cf6';
      if(s === 'IN_REVIEW') return '#fbbf24';
      return '#aaa';
  };

  const statusLabel = (s: string) => {
      const map: Record<string, string> = {
          'IN_REVIEW': '審查中 (法務)',
          'PENDING_MANAGER': '待主管核決',
          'APPROVED': '已核准 (流程完成)',
          'FULLY_APPROVED': '已核准 (流程完成)',
          'REJECTED': '已駁回',
          'RETURNED': '已退回',
          'LEGAL_RETURNED': '法務退回',
          'DRAFT': '草稿',
          'CANCELLED': '已作廢'
      };
      return map[s] || s;
  };

  const isTerminal = ['APPROVED', 'FULLY_APPROVED', 'REJECTED', 'RETURNED', 'LEGAL_RETURNED', 'CANCELLED'].includes(currentRequest.status);

  return (
    <Modal title={`簽呈詳情: ${reqTitle}`} onClose={onClose} width={1100}>
       <div style={{display:'grid', gridTemplateColumns:'1fr 400px', gap: 24}}>
          {/* Left: Request Form Data */}
          <div>
             <div style={{marginBottom:16, display:'flex', gap:10, alignItems:'center'}}>
                <span className="badge" style={{color: statusColor(reqStatus), borderColor: statusColor(reqStatus), fontSize:14}}>
                    {statusLabel(reqStatus)}
                </span>
                <span style={{fontSize:13, color:'var(--muted)'}}>單號: {reqId}</span>
                {showRepair && (
                    <button className="smallbtn" style={{fontSize:10, padding:'2px 6px', borderColor:'#f59e0b', color:'#f59e0b'}} onClick={handleRepairLink}>
                        🔗 建立流程關聯
                    </button>
                )}
             </div>
             
             <div className="section" style={{padding:16, marginBottom:16}}>
                <div className="p" style={{color:'#fff', fontWeight:'bold', fontSize:16, marginBottom:8}}>{reqTitle}</div>
                <div className="p">類別: {reqCategory} | 金額: {reqAmount || 0}</div>
                <div className="p">申請人: {requesterName} ({reqDeptName})</div>
                
                <div style={{marginTop:8, paddingTop:8, borderTop:'1px dashed #333', fontSize:12}}>
                    {(currentRequest as any).reviewCode && <div style={{color:'#a5b4fc'}}>會審編號: {(currentRequest as any).reviewCode}</div>}
                    {(currentRequest as any).archiveCode && <div style={{color:'#34d399'}}>歸檔編號: {(currentRequest as any).archiveCode}</div>}
                </div>

                {reqConsultedDepts && (
                    <div className="p" style={{marginTop:8, color:'#a5b4fc'}}>已徵詢部門：{reqConsultedDepts}</div>
                )}
                
                <div className="p" style={{marginTop:12, fontWeight:500, color:'#ccc'}}>說明/擬辦：</div>
                <div style={{marginTop:4, background:'rgba(255,255,255,0.05)', padding:12, borderRadius:6, whiteSpace:'pre-wrap', fontSize:14, lineHeight:1.5, color:'#ddd'}}>
                    {reqDescription}
                </div>

                {reqOtherComments && (
                    <div style={{marginTop:12}}>
                        <div className="p" style={{fontWeight:500, color:'#ccc'}}>其他部門意見摘要：</div>
                        <div style={{marginTop:4, background:'rgba(255,255,255,0.05)', padding:12, borderRadius:6, whiteSpace:'pre-wrap', fontSize:14, lineHeight:1.5, color:'#ddd'}}>
                            {reqOtherComments}
                        </div>
                    </div>
                )}

                {reqAttachments && reqAttachments.length > 0 && (
                    <div style={{marginTop:16}}>
                       <FileUploadKit existingFiles={reqAttachments} disabled />
                    </div>
                )}
             </div>

             {/* Direct Seal Gate UI (Only for Lisa/Irene when flow is done but gate is open) */}
             {showGateUI && (
                <div className="section" style={{padding:16, marginBottom:16, border:'1px solid #f59e0b', background:'rgba(245, 158, 11, 0.05)'}}>
                    <h4 style={{color:'#f59e0b', marginTop:0}}>👑 雙主管直接用印確認</h4>
                    <p style={{fontSize:13, color:'#ccc'}}>此案已完成審查。請確認是否允許<b>直接用印</b> (免另填用印單)。</p>
                    
                    <div style={{marginTop:12}}>
                        <div style={{display:'flex', gap:12, marginBottom:8}}>
                            <span style={{color: gate?.lisaDecision==='ALLOW'?'var(--ok)':gate?.lisaDecision==='DENY'?'var(--danger)':'#aaa'}}>Lisa: {gate?.lisaDecision || 'PENDING'}</span>
                            <span style={{color: gate?.ireneDecision==='ALLOW'?'var(--ok)':gate?.ireneDecision==='DENY'?'var(--danger)':'#aaa'}}>Irene: {gate?.ireneDecision || 'PENDING'}</span>
                        </div>
                        <textarea className="input" placeholder="意見/備註..." value={gateNote} onChange={e=>setGateNote(e.target.value)} rows={2} style={{marginBottom:10}} />
                        <div style={{display:'flex', gap:10}}>
                            <button className="btn" style={{background:'var(--ok)', color:'#000'}} onClick={()=>handleGateDecision('ALLOW')} disabled={isProcessing}>同意直接用印</button>
                            <button className="btn" style={{background:'transparent', border:'1px solid var(--danger)', color:'var(--danger)'}} onClick={()=>handleGateDecision('DENY')} disabled={isProcessing}>否決 (需補件)</button>
                        </div>
                    </div>
                </div>
             )}
          </div>

          {/* Right: Approval Flow Panel */}
          <div style={{borderLeft:'1px solid #333', paddingLeft:24}}>
             <h4 className="h1" style={{color:'#8b5cf6', marginBottom:16}}>簽核進度</h4>
             
             {reqStatus === 'DRAFT' && (
                 <div style={{marginBottom: 20}}>
                     <div className="p" style={{marginBottom:10}}>請確認內容無誤後送出簽核。</div>
                     <div className="p" style={{color:'orange'}}>請在列表頁點擊「送出」或使用上方按鈕啟動流程。</div>
                 </div>
             )}

             {isTerminal && (
                 <div style={{marginBottom: 16, padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 6, color: '#aaa', fontSize: 13}}>
                     ℹ️ 此案件已結案或終止。
                 </div>
             )}

             {flowLoading || resolving ? <div className="p">載入流程中...</div> : (
                <div style={{pointerEvents: isTerminal ? 'none' : 'auto', opacity: isTerminal ? 0.8 : 1}}>
                    {flow ? (
                        <ApprovalFlowPanel 
                            flow={flow} 
                            onDecision={handleDecision}
                            requestId={reqId}
                            reviewAttachments={reqReviewAttachments} 
                        />
                    ) : (
                        <div className="p" style={{color:'#f87171'}}>
                            找不到關聯的簽核流程。<br/>
                            (若為舊資料或剛建立，可能尚無流程紀錄)
                        </div>
                    )}
                </div>
             )}
          </div>
       </div>
       <div className="row" style={{marginTop:20, justifyContent:'space-between'}}>
          <div>
              {isSuper && (
                  <button 
                    className="btn" 
                    style={{width:'auto', background:'transparent', border:'1px solid var(--danger)', color:'var(--danger)'}} 
                    onClick={handleDelete}
                  >
                    刪除此單 (SuperUser)
                  </button>
              )}
          </div>
          <button className="btn" style={{width:'auto'}} onClick={onClose}>關閉</button>
       </div>
    </Modal>
  );
}
